// Ops › Tenants › Deleted (/tenants?view=deleted, owner, November 3): the
// signups whose league is gone, deleted by the cleanup (it never paid) or from
// Ops. They're not leagues any more, so the rows come from the signup record
// (GET /platform/signups?state=deleted), shown like the other tenant views:
// the league, who signed up, when it was deleted and by what. Each opens its
// page (/tenants/signups/:id), where the person, the history and the compose
// are kept. Search and the page live in the URL.

import { useSearchParams } from 'react-router-dom'
import { api } from '@/api'
import type { SignupRow } from '@/api/growthSignups'
import { PageHeader } from '@/components/shell/PageHeader'
import { Badge, Button, Card, DataTable, EmptyState, ErrorState, Pagination, SearchInput, SkeletonRows, type Column } from '@/components/ui'
import { formatDate, titleCase } from '@/lib/format'
import { useDocumentTitle } from '@/lib/hooks'
import { useQuery } from '@/lib/query'
import { PLATFORM_DOMAIN } from '@/lib/tenant'
import { TenantViewChips } from './TenantViews'

const PAGE_SIZE = 25

function deletedBy(row: SignupRow): string {
  const at = row.lifecycle.league_deleted_at
  return at ? formatDate(at) : '—'
}

const COLUMNS: Column<SignupRow>[] = [
  {
    id: 'name',
    header: 'Tenant',
    primary: true,
    className: 'min-w-[13rem] max-w-[18rem]',
    cell: (r) => (
      <span className="grid min-w-0 gap-0.5">
        <span className="break-words font-semibold">{r.league.name}</span>
        <span className="truncate font-mono text-xs font-medium text-muted">{r.league.subdomain ? `${r.league.subdomain}.${PLATFORM_DOMAIN}` : 'no subdomain'}</span>
      </span>
    ),
  },
  { id: 'status', header: 'Status', cell: () => <Badge tone="neutral">Deleted</Badge> },
  {
    id: 'person',
    header: 'Signed up by',
    cell: (r) => (
      <span className="grid min-w-0 gap-0.5">
        <span className="break-words">{r.person.name ?? r.person.email ?? 'No name'}</span>
        {r.person.name && r.person.email ? <span className="font-mono text-xs text-muted [overflow-wrap:anywhere]">{r.person.email}</span> : null}
      </span>
    ),
  },
  { id: 'deleted', header: 'Deleted', cell: (r) => <span className="whitespace-nowrap tabular text-muted">{deletedBy(r)}</span> },
  { id: 'sport', header: 'Sport', cell: (r) => <span className="text-muted">{r.league.sport ? titleCase(r.league.sport) : '—'}</span> },
  { id: 'created', header: 'Signed up', cell: (r) => <span className="whitespace-nowrap tabular text-muted">{formatDate(r.signed_up_at)}</span> },
]

export default function DeletedTenantsList() {
  useDocumentTitle('Deleted tenants')
  const [searchParams, setSearchParams] = useSearchParams()
  const q = searchParams.get('q') ?? ''
  const pageRaw = Number(searchParams.get('page'))
  const page = Number.isInteger(pageRaw) && pageRaw > 0 ? pageRaw : 1
  const set = (patch: Record<string, string | undefined>) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [k, v] of Object.entries(patch)) {
          if (!v || (k === 'page' && v === '1')) next.delete(k)
          else next.set(k, v)
        }
        if (!('page' in patch)) next.delete('page')
        return next
      },
      { replace: true },
    )

  const { data, error, status, isFetching, refetch } = useQuery(
    ['signups', 'deleted', q, page],
    (signal) => api.growth.signups.list({ state: 'deleted', q: q || undefined, page, page_size: PAGE_SIZE, sort: '-signed_up' }, signal),
    { keepPrevious: true },
  )
  const rows = data?.signups ?? []

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader title="Tenants" description="Deleted: signups whose league was deleted, by the cleanup (it never paid) or from Ops. Who they were and every email is kept." />
      <TenantViewChips value="deleted" />
      <SearchInput label="Search deleted tenants" placeholder="Search name, email or subdomain" value={q} onChange={(v) => set({ q: v })} className="sm:max-w-sm" />
      <Card flush className="overflow-hidden">
        {status === 'loading' ? (
          <SkeletonRows rows={4} label="Loading deleted tenants" />
        ) : status === 'error' && !data ? (
          <ErrorState error={error} title="Couldn't load deleted tenants" onRetry={refetch} />
        ) : rows.length === 0 ? (
          q ? (
            <EmptyState
              title="No deleted tenants match"
              description={`Nothing matches “${q}”.`}
              action={
                <Button size="sm" onClick={() => set({ q: undefined })}>
                  Clear search
                </Button>
              }
            />
          ) : (
            <EmptyState title="No deleted tenants" description="A signup's league that the cleanup or Ops deletes shows here, with everything about it kept." />
          )
        ) : (
          <>
            <DataTable caption="Deleted tenants" rows={rows} columns={COLUMNS} rowKey={(r) => r.id} rowHref={(r) => `/tenants/signups/${r.id}`} busy={isFetching && status === 'success'} />
            <div className="border-t border-line px-4 py-3">
              <Pagination page={data?.page ?? page} pageSize={data?.page_size ?? PAGE_SIZE} total={data?.total ?? 0} noun="tenants" onPageChange={(p) => set({ page: String(p) })} />
            </div>
          </>
        )}
      </Card>
    </div>
  )
}
